/**
 * Accept an invite: set a password, and — for an admin — bind an authenticator.
 *
 * The screen greets the person by name and vessel before they type anything --
 * that comes from `GET /api/auth/invite/{token}`, which returns nothing
 * sensitive and 404s flatly on a bad token rather than confirming a guess.
 *
 * ## Why TOTP enrolment lives here
 *
 * It used to say that admins accept their invites in the admin application, so
 * the TOTP branch was "a safety net rather than a path anyone walks". That was
 * wrong in a way that only showed up on the first real deployment: `admin-web`
 * had no `/invite` route at all, and `public_base_url` was one value for both
 * apps, so **every** invite link -- captain or admin -- landed here.
 *
 * Both halves of that have since been fixed: staff invites go to
 * `ADMIN_BASE_URL`, and `admin-web` has its own `/invite` route which finishes
 * the enrolment and lands the agent in the back office signed in (2026-09-04).
 * So this branch *is* a safety net again -- for a staff link opened here by
 * hand, or by an old email sent before the split. It stays because a
 * half-completed enrolment is the one state in this flow that no UI can
 * recover: accepting spends the invite.
 *
 * What it did instead was tell the reader to "complete setup in the admin
 * application", which is an instruction that cannot be followed, and it did so
 * *after* the accept had already succeeded and consumed the invite. Pressing
 * the button again then produced "This invite link is not valid" -- the token
 * being genuinely spent by then -- so a successful enrolment presented as a
 * broken link, and the account was left with a password and a TOTP secret but
 * `status='invited'`, reachable by no UI at all.
 *
 * ## The shape of the fix
 *
 * `POST /api/auth/invite/accept` already returns the whole enrolment challenge
 * for an admin: a `setup_token`, the shared `secret`, and a `provisioning_uri`.
 * All that was missing was a second step to spend it on
 * `POST /api/auth/invite/totp`, which verifies a code, flips the account to
 * `active` and returns a session.
 *
 * The provisioning URI is offered as a **tappable link** rather than a QR code.
 * These invites are opened on a phone, and on a phone `otpauth://` opens the
 * authenticator directly -- a QR would require a second device to scan it. The
 * secret is shown as text underneath for anyone enrolling on a desktop.
 */

import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Field, Notice, Spinner } from '@abg/ui'
import { api, ApiError } from '../lib/api'
import { useAuth } from '../lib/auth'
import type { InviteInfo, TokenResponse, TotpSetup } from '../lib/types'

export function InvitePage() {
  const { token = '' } = useParams()
  const navigate = useNavigate()
  const { applySession } = useAuth()

  const [invite, setInvite] = useState<InviteInfo | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  /** Set once the password step has succeeded. Holding it is what makes the
   *  invite's consumption survivable: the token is spent, but the enrolment
   *  challenge it produced is still in hand. */
  const [setup, setSetup] = useState<TotpSetup | null>(null)
  const [code, setCode] = useState('')
  /** Staff only: enrolment finished, and this app is not where they work. */
  const [handOff, setHandOff] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void api
      .get<InviteInfo>(`/api/auth/invite/${encodeURIComponent(token)}`)
      .then((data) => !cancelled && setInvite(data))
      .catch((caught) => {
        if (cancelled) return
        setLoadError(
          caught instanceof ApiError && caught.status === 404
            ? 'This invite link is not valid any more. Ask your agent for a new one.'
            : 'Could not open this invite. Check your connection and try again.',
        )
      })
    return () => {
      cancelled = true
    }
  }, [token])

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (password !== confirm) {
      setError('The two passwords do not match.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const result = await api.post<TokenResponse | TotpSetup>('/api/auth/invite/accept', {
        token,
        password,
      })
      if ('access_token' in result) {
        if (invite?.continue_url) {
          // Staff, on a deployment with REQUIRE_ADMIN_TOTP off. The session is
          // admin-shaped and unusable here; send them to their own app.
          setHandOff(invite.continue_url)
        } else {
          applySession(result)
          // Straight to onboarding, which is where the iOS "add to home
          // screen" instruction lives -- the one thing a new captain must do
          // before push will ever work.
          navigate('/onboarding', { replace: true })
        }
      } else {
        // Admin. The invite is spent now, so this state has to carry the rest
        // of the flow -- there is no going back to the link.
        setSetup(result)
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not set your password.')
    } finally {
      setBusy(false)
    }
  }

  const confirmCode = async (event: FormEvent) => {
    event.preventDefault()
    if (!setup) return
    setBusy(true)
    setError(null)
    try {
      const session = await api.post<TokenResponse>('/api/auth/invite/totp', {
        setup_token: setup.setup_token,
        code: code.trim(),
      })
      if (invite?.continue_url) {
        // The account is active now. Do NOT apply the session: the enrolment
        // endpoint issued it with client="admin", so the refresh half is an
        // HttpOnly cookie on the API origin rather than a token in the body --
        // this app has nothing to hold, and the back office is a different
        // origin that cannot read it either. They sign in there once, which is
        // the flow they will use every day anyway.
        setHandOff(invite.continue_url)
      } else {
        applySession(session)
        navigate('/onboarding', { replace: true })
      }
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Could not confirm that code.',
      )
    } finally {
      setBusy(false)
    }
  }

  if (loadError) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-brand">Superyacht Services</div>
          <h1>Invitation</h1>
          <Notice tone="alert">{loadError}</Notice>
        </div>
      </div>
    )
  }

  if (!invite) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <Spinner label="Opening your invitation…" />
        </div>
      </div>
    )
  }

  // ------------------------------------------------------ staff: hand them on
  if (handOff) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-brand">Superyacht Services</div>
          <h1>You are all set</h1>
          <p className="subtitle muted">
            Your account is active. The back office is a separate application —
            sign in there with your email, your new password and a code from your
            authenticator.
          </p>
          <a className="btn btn-lg btn-block" href={handOff}>
            Continue to the back office
          </a>
          <p className="caption muted">
            Worth bookmarking: {handOff.replace(/^https?:\/\//, '')}
          </p>
        </div>
      </div>
    )
  }

  // ------------------------------------------------- step two: authenticator
  if (setup) {
    return (
      <div className="auth-page">
        <form className="auth-card" onSubmit={confirmCode}>
          <div>
            <div className="auth-brand">Superyacht Services</div>
            <h1>One more step</h1>
            <p className="subtitle muted">
              Your password is set. Staff accounts also need an authenticator app.
            </p>
          </div>

          {error && <Notice tone="alert">{error}</Notice>}

          <Notice tone="info">
            <strong>On this phone:</strong> tap the button below and your
            authenticator will offer to add the account.
            <br />
            <strong>On a computer:</strong> open your authenticator and enter the
            key by hand.
          </Notice>

          <a className="btn btn-block" href={setup.provisioning_uri}>
            Add to my authenticator
          </a>

          <Field label="Or enter this key by hand">
            <input
              type="text"
              value={setup.secret}
              readOnly
              onFocus={(e) => e.currentTarget.select()}
              className="mono"
            />
          </Field>

          <Field
            label="Code from the app"
            hint="Six digits. It changes every 30 seconds."
          >
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              required
              autoFocus
            />
          </Field>

          <button
            type="submit"
            className="btn btn-lg btn-block"
            disabled={busy || code.length !== 6}
          >
            {busy ? 'Checking…' : 'Confirm and finish'}
          </button>

          <p className="caption muted">
            Keep the authenticator entry. You will need a code every time you sign
            in, and there is no way to sign in without it.
          </p>
        </form>
      </div>
    )
  }

  // ------------------------------------------------------ step one: password
  return (
    <div className="auth-page">
      <form className="auth-card" onSubmit={submit}>
        <div>
          <div className="auth-brand">Superyacht Services</div>
          <h1>Welcome, {invite.name}</h1>
          {invite.vessel_name && (
            <p className="subtitle muted">You will be filing for {invite.vessel_name}.</p>
          )}
        </div>

        {error && <Notice tone="alert">{error}</Notice>}

        <Field label="Email">
          <input type="email" value={invite.email} readOnly disabled />
        </Field>

        <Field
          label="Choose a password"
          hint="At least 12 characters. A short sentence you will remember works well."
        >
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={12}
            required
            autoFocus
          />
        </Field>

        <Field label="Repeat password">
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
          />
        </Field>

        <button type="submit" className="btn btn-lg btn-block" disabled={busy}>
          {busy ? 'Setting up…' : 'Set password and continue'}
        </button>
      </form>
    </div>
  )
}
